'use client';

import { useState, useEffect } from 'react';

interface UseProfileHeaderCollapseOptions {
  threshold?: number;
  scrollSelector?: string;
}

interface UseProfileHeaderCollapseReturn {
  isCondensed: boolean;
  scrollY: number;
}

const useProfileHeaderCollapse = ({
  threshold = 100,
  scrollSelector,
}: UseProfileHeaderCollapseOptions = {}): UseProfileHeaderCollapseReturn => {
  const [isCondensed, setIsCondensed] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = (scrollTop: number) => {
      setScrollY(scrollTop);
      
      // Simple with reasonable buffer
      if (scrollTop > threshold && !isCondensed) {
        setIsCondensed(true);
      } else if (scrollTop < threshold - 20 && isCondensed) {
        setIsCondensed(false);
      }
    };

    if (scrollSelector) {
      const element = document.querySelector(scrollSelector) as HTMLElement;
      if (element) {
        const handleElementScroll = () => handleScroll(element.scrollTop);
        element.addEventListener('scroll', handleElementScroll, { passive: true });
        return () => element.removeEventListener('scroll', handleElementScroll);
      }
    }

    // Fallback to window scroll
    const handleWindowScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      handleScroll(scrollTop);
    };
    
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [threshold, scrollSelector, isCondensed]);

  return { isCondensed, scrollY };
};

export default useProfileHeaderCollapse;
